import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { objects, strings, validators } from 'investira.sdk';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Icon,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useMediaQuery
} from 'investira.react.components';
import services from '../../../services';
import utils from '../../../utils';
import SearchIndicadoresItem from './SearchIndicadoresItem';

const wPageSize = 10;

/** Formata o texto para o contrato de pesquisa full text da API. */
function pvFormatPesquisa(pPesquisa) {
  return String(pPesquisa || '')
    .replace(/[./-]/g, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(pTermo => `+${pTermo}*`)
    .join('');
}

/** Remove o segmento de paginação para atualizar a pesquisa na primeira página. */
function pvRemovePagination(pPathname = '') {
  return pPathname.replace(/\/page\/\d+$/, '');
}

/**
 * Pesquisa indicadores em contexto geral ou limitado pela função de leitura recebida.
 * A URL mantém o termo confirmado, enquanto as sugestões permanecem transitórias.
 */
const SearchIndicadores = memo(pProps => {
  const {
    dataRead = services.pesquisas.indicador,
    pageIsLoading = false,
    preserveQuery = false,
    resultsPath = null,
    tipoDescricao = null,
    tipoId = null
  } = pProps;
  const xLocation = useLocation();
  const xNavigate = useNavigate();
  const xQuery = strings.querystringToObject(xLocation.search) || {};
  const xSearchValue = xQuery.pesquisa || '';
  const xPlaceholder = tipoDescricao
    ? `Pesquisar indicadores de ${tipoDescricao.toLocaleLowerCase('pt-BR')}`
    : 'Pesquisar indicadores (IPCA, IGP-M...)';
  const [open, setOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [pesquisa, setPesquisa] = useState(xSearchValue);
  const [data, setData] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [message, setMessage] = useState(null);
  const inputRef = useRef();
  const placeholderRef = useRef();
  const requestIdRef = useRef(0);
  const tipoIdRef = useRef(tipoId);
  const isUpMd = useMediaQuery(pTheme => pTheme.breakpoints.up('md'));
  const xRequest = useMemo(() => utils.helpers.debounce(dataRead, 1000), [dataRead]);

  /** Fecha o diálogo e restaura o termo confirmado na URL. */
  function pvClose() {
    requestIdRef.current += 1;
    setOpen(false);
    setLoading(false);
    setPesquisa(xSearchValue);
    setData([]);
    setTotalResults(0);
    setMessage(null);
    placeholderRef.current?.blur();
    inputRef.current?.blur();
  }

  /** Abre o detalhe do indicador selecionado. */
  function pvGoToIndicador(pEvent, pIndicadorId) {
    pEvent.preventDefault();
    pEvent.stopPropagation();
    pvClose();
    xNavigate(`/indicadores/${pIndicadorId}`);
  }

  /** Confirma o termo e apresenta todos os resultados na rota contextual. */
  function pvSearchAll() {
    if (!resultsPath || !pesquisa.trim()) {
      return;
    }

    const xCurrentQuery = preserveQuery ? xQuery : {};
    const xSearch = objects.objectToQueryString({
      ...xCurrentQuery,
      pesquisa: pesquisa.trim()
    });

    pvClose();
    xNavigate(`${pvRemovePagination(resultsPath)}${xSearch}`);
  }

  /** Remove somente a pesquisa textual e preserva os demais parâmetros compatíveis. */
  function pvClearSearch(pEvent) {
    pEvent.preventDefault();
    pEvent.stopPropagation();

    const xQueryWithoutSearch = preserveQuery ? { ...xQuery } : {};
    const xResultsPath = resultsPath || pvRemovePagination(xLocation.pathname);

    delete xQueryWithoutSearch.pesquisa;
    setPesquisa('');
    setData([]);
    setTotalResults(0);
    xNavigate(`${xResultsPath}${objects.objectToQueryString(xQueryWithoutSearch)}`);
  }

  // A URL restaura o texto ao navegar pelo histórico ou trocar de página.
  useEffect(() => {
    setPesquisa(xSearchValue);
  }, [xSearchValue]);

  // Ao abrir o diálogo, transfere o foco do campo de entrada para a busca editável.
  useEffect(() => {
    if (open) {
      placeholderRef.current?.blur();
      inputRef.current?.focus();
    }
  }, [open]);

  // Sugestões e respostas pendentes nunca atravessam o contexto do tipo.
  useEffect(() => {
    requestIdRef.current += 1;
    tipoIdRef.current = tipoId;
    setOpen(false);
    setLoading(false);
    setData([]);
    setTotalResults(0);
    setMessage(null);
  }, [tipoId]);

  // A consulta só acontece com o diálogo aberto e pelo menos dois caracteres.
  useEffect(() => {
    const xRequestId = ++requestIdRef.current;
    const xPesquisa = pesquisa.trim();

    if (!open || xPesquisa.length < 2 || pageIsLoading) {
      setLoading(false);
      setData([]);
      setTotalResults(0);
      setMessage(null);
      return;
    }

    const xRequestTipoId = tipoId;

    setLoading(true);
    setData([]);
    setTotalResults(0);
    setMessage(null);

    xRequest(
      {
        pesquisa: pvFormatPesquisa(xPesquisa),
        size: wPageSize,
        sort: 'relevancia DESC, __mr__ DESC, indicador ASC'
      },
      pResponse => {
        if (requestIdRef.current !== xRequestId || tipoIdRef.current !== xRequestTipoId) {
          return;
        }

        const xData = Object.values(pResponse.data || {});

        setData(xData);
        setTotalResults(pResponse.pages?.total_items || xData.length);
        setMessage(xData.length ? null : 'Nenhum indicador encontrado');
      },
      () => {
        if (requestIdRef.current === xRequestId && tipoIdRef.current === xRequestTipoId) {
          setMessage('Ocorreu um erro durante a busca');
        }
      },
      () => {
        if (requestIdRef.current === xRequestId && tipoIdRef.current === xRequestTipoId) {
          setLoading(false);
        }
      }
    );
  }, [open, pageIsLoading, pesquisa, tipoId, xRequest]);

  const xDialogProps = isUpMd
    ? {
        fullScreen: false,
        PaperProps: { sx: { pt: 3, pb: 2, px: 2 } },
        maxWidth: 'lg'
      }
    : {
        fullScreen: true,
        PaperProps: { sx: { margin: 0, backgroundColor: 'background.dark' } }
      };

  return (
    <Stack id="search-indicadores" sx={{ position: 'relative', width: '100%', zIndex: 999 }}>
      {pageIsLoading ? (
        <Skeleton variant="rounded" width="100%" height={56} />
      ) : (
        <TextField
          id="pesquisa-indicadores"
          inputRef={placeholderRef}
          value={pesquisa}
          onFocus={() => setOpen(true)}
          variant="outlined"
          size="normal"
          placeholder={xPlaceholder}
          fullWidth
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <Icon iconName="find" size={21} color="primary" />
              </InputAdornment>
            ),
            endAdornment: !validators.isEmpty(xSearchValue) && (
              <InputAdornment position="end">
                <IconButton aria-label="Limpar pesquisa" color="primary" onClick={pvClearSearch}>
                  <Icon iconName="cancel" size={18} color="primary" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      )}

      <Dialog
        {...xDialogProps}
        open={open}
        onClose={pvClose}
        disableRestoreFocus
        keepMounted
        fullWidth>
        <DialogContent>
          <Stack direction="row" alignItems="center" spacing={2} mb={1}>
            <Stack>
              <IconButton aria-label="Fechar pesquisa" onClick={pvClose} color="primary">
                <Icon iconName="arrow_left" size={21} />
              </IconButton>
            </Stack>
            <Stack flexGrow={1}>
              <TextField
                id="pesquisa-indicadores-dialog"
                inputRef={inputRef}
                value={pesquisa}
                onChange={pEvent => setPesquisa(pEvent.currentTarget.value)}
                sx={{ border: 'none' }}
                variant="outlined"
                size={isUpMd ? 'normal' : 'small'}
                placeholder={xPlaceholder}
                fullWidth
                disabled={pageIsLoading}
              />
            </Stack>
          </Stack>
          <Divider />
          <Stack>
            {!isLoading && data.length > 0 && (
              <>
                <Stack id="pesquisa-indicadores-list" sx={{ px: 0 }} component="ul">
                  {data.map(pIndicador => (
                    <SearchIndicadoresItem
                      key={pIndicador.indicador_id}
                      indicador={pIndicador}
                      onClick={pEvent => pvGoToIndicador(pEvent, pIndicador.indicador_id)}
                    />
                  ))}
                </Stack>
                {resultsPath && (
                  <Stack sx={{ px: 2, py: 2, alignItems: 'center' }}>
                    <Button variant="contained" onClick={pvSearchAll} sx={{ maxWidth: 500, px: 4 }}>
                      Visualizar todos ({totalResults})
                    </Button>
                  </Stack>
                )}
              </>
            )}

            {isLoading && (
              <Stack justifyContent="center" alignItems="center" flexGrow={1} sx={{ py: 2 }}>
                <CircularProgress size={24} />
              </Stack>
            )}

            {!isLoading && data.length === 0 && message && (
              <Stack justifyContent="center" alignItems="center" flexGrow={1} sx={{ py: 2 }}>
                <Typography variant="body2" color="textPrimary">
                  {message}
                </Typography>
              </Stack>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </Stack>
  );
});

SearchIndicadores.propTypes = {
  dataRead: PropTypes.func,
  pageIsLoading: PropTypes.bool,
  preserveQuery: PropTypes.bool,
  resultsPath: PropTypes.string,
  tipoDescricao: PropTypes.string,
  tipoId: PropTypes.string
};

SearchIndicadores.displayName = 'SearchIndicadores';

export default SearchIndicadores;
